import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const FinalCTA = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });
  const { t } = useLanguage();

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="py-32 bg-[#050505] relative overflow-hidden">
      {/* Decorative lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A55C]/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      
      {/* Background accent */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, #C5A55C 0%, transparent 50%)',
        }}
      />

      <div className="max-w-4xl mx-auto px-6 lg:px-12 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <motion.span 
            className="font-montserrat font-light text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-8"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {t('cta.tagline')}
          </motion.span>
          
          <h2 className="font-montserrat font-extralight text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.1em] uppercase mb-8 leading-tight">
            {t('cta.title1')}
            <br />
            <span className="text-[#C5A55C]">{t('cta.title2')}</span>
          </h2>
          
          <p className="font-raleway font-light text-lg text-white/40 tracking-wide mb-12 max-w-2xl mx-auto leading-relaxed">
            {t('cta.description')}
          </p>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={scrollToContact}
            className="group px-16 py-6 border border-[#C5A55C] text-[#C5A55C] bg-transparent hover:bg-[#C5A55C] hover:text-black transition-all duration-500 font-montserrat font-light text-sm tracking-[0.2em] uppercase"
            data-testid="final-cta-btn"
          >
            <span className="flex items-center gap-4">
              {t('cta.button')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
